
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

const FOOD_IMAGES = [
  {
    src: "/flagship/ember-stone-main-pizza.png",
    alt: "Ember and Stone signature wood-fired pizza",
  },
  {
    src: "/flagship/ember-stone-interior.png",
    alt: "Ember and Stone dining room and wood-fired oven",
  },
  {
    src: "/flagship/ember-stone-garlic-knots.png",
    alt: "Ember and Stone garlic knots and pizza offer",
  },
];

const SCENE_EXPLANATIONS = [
  {
    step: "1 of 4 · Discovery",
    title: "Customers Find Your TapToTable Link",
    body:
      "Share your link in social posts, your bio, website, Google Business Profile, QR codes, email, and more.",
  },
  {
    step: "2 of 4 · Customer Page",
    title: "Give Customers a Reason to Act",
    body:
      "Feature a promotion, menu item, seasonal special, or another timely reason to visit.",
  },
  {
    step: "3 of 4 · Customer Action",
    title: "Turn Interest Into Action",
    body:
      "For offers, customers can claim a code. Events and featured items lead directly to their next action.",
  },
  {
    step: "4 of 4 · Client Dashboard",
    title: "Your Dashboard, Your Control",
    body:
      "Create promotions, events, or featured items. Update photos and details, preview changes, and monitor available activity.",
  },
];

export default function DemoMockup() {
  const [scene, setScene] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;

    const id = setInterval(() => {
      setScene((s) => (s + 1) % 4);
    }, 6000);

    return () => clearInterval(id);
  }, [paused]);

  const goToScene = (nextScene) => {
    setScene(nextScene);
    setPaused(true);
  };

  const mainPizza = FOOD_IMAGES[0];
  const interior = FOOD_IMAGES[1];
  const garlicKnots = FOOD_IMAGES[2];

  return (
    <div
      className="relative"
      data-testid="demo-mockup"
      aria-label="Animated TapToTable Preview"
    >
      <div className="absolute -inset-10 bg-[#00E5FF]/10 blur-3xl rounded-full pointer-events-none" />

      <div className="relative w-[280px] sm:w-[300px] h-[580px] rounded-[3rem] border-[10px] border-zinc-800 bg-black overflow-hidden shadow-2xl">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-28 bg-black rounded-b-2xl z-20 border border-t-0 border-zinc-800" />

        {scene !== 0 && (
          <>
            {scene !== 3 ? (
              <div className="absolute top-8 left-4 z-30 rounded-full border border-white/10 bg-black/70 px-3 py-1 text-[10px] text-zinc-300">
                TapToTable Preview
              </div>
            ) : (
              <div className="absolute bottom-8 left-4 z-30 rounded-full border border-white/10 bg-black/70 px-3 py-1 text-[10px] text-zinc-300">
                TapToTable Preview
              </div>
            )}

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 0.82, y: 0 }}
              whileHover={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="absolute top-8 right-4 z-30 flex items-center gap-1.5 rounded-full border border-white/10 bg-[#121214]/80 px-2.5 py-1 shadow backdrop-blur"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" />
              <span className="text-[10px] text-zinc-300">Live</span>
            </motion.div>
          </>
        )}

        <AnimatePresence mode="wait">
          {scene === 0 && (
            <RestaurantPageScene
              key="restaurant-page"
              food={mainPizza}
              goToScene={goToScene}
            />
          )}

          {scene === 1 && (
            <OfferScene
              key="offer"
              food={garlicKnots}
              goToScene={goToScene}
            />
          )}

          {scene === 2 && (
            <ClaimScene
              key="claim"
              food={interior}
              goToScene={goToScene}
            />
          )}

          {scene === 3 && (
            <DashboardScene key="dashboard" />
          )}
        </AnimatePresence>

        <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => goToScene(i)}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === scene ? "w-6 bg-[#00E5FF]" : "w-1.5 bg-white/25"
              }`}
              aria-label={`Show demo step ${i + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="mx-auto mt-5 w-[280px] sm:w-[300px] text-center">
        <motion.div
          key={`arrow-${scene}`}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[#00E5FF] text-lg leading-none"
          aria-hidden="true"
        >
          ↓
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`explanation-${scene}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3 }}
            className="mt-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
          >
            <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#00E5FF]">
              {SCENE_EXPLANATIONS[scene].step}
            </div>
            <div className="mt-1 text-sm font-semibold text-white">
              {SCENE_EXPLANATIONS[scene].title}
            </div>
            <div className="mt-1.5 text-[11px] leading-relaxed text-zinc-400">
              {SCENE_EXPLANATIONS[scene].body}
            </div>

            <div className="mt-2 text-[9px] text-zinc-600">
              {paused
                ? "Paused after your selection · Restart Demo to play from the beginning"
                : "Auto-advances every 6 seconds"}
            </div>
          </motion.div>
        </AnimatePresence>

        <button
          type="button"
          onClick={() => {
            setScene(0);
            setPaused(false);
          }}
          className="mt-3 inline-flex items-center justify-center rounded-full border border-[#00E5FF]/25 bg-[#00E5FF]/10 px-4 py-2 text-[11px] font-semibold text-[#66FFFF] transition-colors hover:bg-[#00E5FF]/15"
          aria-label="Restart TapToTable demo"
        >
          ↻ Restart Demo
        </button>
      </div>
    </div>
  );
}

function Scene({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.45 }}
      className="absolute inset-0 bg-black"
    >
      {children}
    </motion.div>
  );
}

function handleImageError(event) {
  const image = event.currentTarget;

  if (image.dataset.fallbackApplied === "true") {
    return;
  }

  image.dataset.fallbackApplied = "true";
  image.src = "/flagship/ember-stone-main-pizza.png";
}

function RestaurantPageScene({ goToScene }) {
  const profileImage = FOOD_IMAGES[1];
  const postImage = FOOD_IMAGES[0];

  return (
    <Scene>
      <div className="h-full overflow-hidden bg-black px-4 pb-8 pt-11 text-white">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-gradient-to-br from-[#F03A47] via-[#F59E0B] to-[#8B5CF6] p-[2px]">
            <img
              src={profileImage.src}
              alt="Ember and Stone Instagram profile"
              onError={handleImageError}
              className="h-16 w-16 rounded-full object-cover"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">
              emberandstonepizzeria
            </div>

            <div className="mt-1 text-[10px] leading-relaxed text-zinc-300">
              Ember &amp; Stone Pizzeria
            </div>

            <div className="mt-2 grid grid-cols-3 gap-2 text-center text-[9px]">
              <div>
                <div className="font-semibold text-white">142</div>
                <div className="text-zinc-500">posts</div>
              </div>

              <div>
                <div className="font-semibold text-white">3.9k</div>
                <div className="text-zinc-500">followers</div>
              </div>

              <div>
                <div className="font-semibold text-white">284</div>
                <div className="text-zinc-500">following</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 text-[10px] leading-relaxed text-zinc-300">
          <div className="font-semibold text-white">
            Bigger Better Pizza
          </div>

          <div>📍 Charleston, SC</div>
          <div>🔗 emberandstone.com</div>

          <div className="mt-1 text-zinc-500">
            Handcrafted wood-fired pizza, warm hospitality, and free garlic knots with any large pizza.
          </div>
        </div>

        <div className="mt-3 overflow-hidden rounded-2xl border border-white/10">
          <img
            src={postImage.src}
            alt={postImage.alt}
            onError={handleImageError}
            className="h-52 w-full object-cover"
          />

          <div className="border-t border-white/10 bg-white/[0.03] px-3 py-2.5">
            <div className="text-[10px] font-semibold text-white">
              emberandstonepizzeria
            </div>

            <div className="mt-1 text-[9px] leading-relaxed text-zinc-400">
              Fresh from the oven. Tap our link to claim free garlic knots with any large pizza.
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => goToScene(1)}
          className="mt-3 block w-full pb-6 text-left text-[10px] font-semibold text-[#5BC0FF] hover:underline"
        >
          🔗 taptotable.co/r/ember-stone
        </button>
      </div>
    </Scene>
  );
}

function OfferScene({ food, goToScene }) {
  return (
    <Scene>
      <div className="h-full bg-black px-4 pb-8 pt-11 text-white">
        <div className="relative overflow-hidden rounded-2xl border border-white/10">
          <img
            src={food.src}
            alt={food.alt}
            onError={handleImageError}
            className="h-40 w-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/15 to-transparent" />

          <div className="absolute bottom-3 left-3 right-3">
            <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#F03A47]">
              ★ 4.6 · 2,278 reviews
            </div>

            <div className="mt-1 text-lg font-semibold leading-tight">
              Ember & Stone Pizzeria
            </div>

            <div className="mt-1 text-[9px] text-zinc-200">
              @emberandstonepizzeria
            </div>
          </div>
        </div>

        <div className="mt-2 text-center text-[10px] text-zinc-400">
          Charleston · Wood-Fired Pizzeria
        </div>

        <div className="mt-3 rounded-2xl border border-[#F03A47]/35 bg-[#22090D] p-3">
          <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#F03A47]">
            Today&apos;s Pizza Special
          </div>

          <div className="mt-1.5 text-base font-semibold leading-tight">
            Free Garlic Knots with Any Large Pizza
          </div>

          <div className="mt-1.5 text-[10px] leading-relaxed text-zinc-300">
            Enjoy handcrafted wood-fired pizza and receive complimentary garlic knots with any large pizza.
          </div>

          <motion.button
            type="button"
            onClick={() => goToScene(2)}
            animate={{
              boxShadow: [
                "0 0 0 0 rgba(240,58,71,0.38)",
                "0 0 0 8px rgba(240,58,71,0)",
              ],
            }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="mt-3 w-full rounded-xl bg-[#F03A47] py-2.5 text-sm font-semibold text-black"
          >
            Claim My Free Knots
          </motion.button>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2 text-[9px]">
          {["Order Online", "View Menu", "Directions", "Website"].map(
            (label) => (
              <div
                key={label}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-2 text-center text-white"
              >
                {label}
              </div>
            )
          )}
        </div>
      </div>
    </Scene>
  );
}

function ClaimScene({ food }) {
  return (
    <Scene>
      <div className="h-full bg-black px-4 pb-8 pt-10 text-white">
        <div className="relative overflow-hidden rounded-2xl border border-white/10">
          <img
            src={food.src}
            alt={food.alt}
            onError={handleImageError}
            className="h-28 w-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

          <div className="absolute bottom-3 left-3 right-3 text-left">
            <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#F03A47]">
              ★ 4.6 · 2,278 reviews
            </div>

            <div className="mt-1 text-sm font-semibold">
              Ember & Stone Pizzeria
            </div>

            <div className="mt-0.5 text-[9px] text-zinc-300">
              @emberandstonepizzeria
            </div>
          </div>
        </div>

        <div className="mt-2 text-center text-[9px] text-zinc-500">
          Charleston · Wood-Fired Pizzeria
        </div>

        <div className="mt-3 rounded-2xl border border-[#F03A47]/40 bg-[#22090D] p-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F03A47] text-sm font-bold text-black">
              ✓
            </div>

            <div>
              <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#F03A47]">
                Offer Claimed
              </div>

              <div className="mt-0.5 text-sm font-semibold text-white">
                Ember & Stone Pizzeria
              </div>
            </div>
          </div>

          <div className="mt-3 text-[11px] font-medium text-white">
            Free Garlic Knots with Any Large Pizza
          </div>

          <div className="mt-3 rounded-xl border border-[#F03A47]/40 bg-[#2A0E13] p-3 text-center">
            <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#F03A47]">
              Offer Code
            </div>

            <div className="mt-1 font-mono text-xl font-bold tracking-[0.16em] text-white">
              EMB-4827
            </div>
          </div>

          <div className="mt-2 text-[9px] text-zinc-500">
            Claimed just now
          </div>

          <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-[9px] text-zinc-300">
            Show this code to your server to claim your offer.
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2 text-[9px]">
            {["Order Online", "Call Restaurant", "Directions", "Website"].map(
              (label) => (
                <div
                  key={label}
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-2 text-center text-white"
                >
                  {label}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </Scene>
  );
}

function DashboardScene() {
  return (
    <Scene>
      <div className="h-full overflow-hidden bg-black px-4 pb-8 pt-10 text-white">
        <div className="text-[8px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">
          TapToTable Dashboard
        </div>

        <div className="mt-1 text-lg font-semibold leading-tight">
          Ember & Stone Pizzeria Dashboard
        </div>

        <div className="mt-1 text-[9px] leading-relaxed text-zinc-400">
          Manage promotions, events, or featured items, plus your restaurant details and customer experience.
        </div>

        <div className="mt-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3">
          <div className="flex items-center gap-2 text-[10px] font-semibold text-emerald-300">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            Your TapToTable Page is live
          </div>

          <div className="mt-1 text-[9px] leading-relaxed text-zinc-300">
            Customers can view your promotion, event, or featured item and take action from your public page.
          </div>

          <div className="mt-2 text-[9px] font-semibold text-[#00E5FF]">
            taptotable.co/r/ember-stone
          </div>
        </div>

        <div className="mt-3">
          <div className="text-[10px] font-semibold">
            Example: Promotion Performance
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2">
            {[
              ["128", "Page Visits"],
              ["24", "Claims"],
              ["18.8%", "Claim Rate"],
            ].map(([value, label]) => (
              <div
                key={label}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-2 text-center"
              >
                <div className="text-base font-semibold text-white">
                  {value}
                </div>

                <div className="mt-1 text-[8px] leading-tight text-zinc-500">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
          <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#00E5FF]">
            Choose What You&apos;re Promoting
          </div>

          <div className="mt-2 grid grid-cols-3 gap-1.5 text-[8px]">
            <div className="rounded-lg border border-[#00E5FF]/35 bg-[#00E5FF]/10 px-1 py-2 text-center text-white">
              <div className="font-semibold leading-tight">Offer / Promotion</div>
              <div className="mt-1 text-zinc-400">Deals · Claims</div>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/30 px-1 py-2 text-center text-white">
              <div className="font-semibold leading-tight">Event</div>
              <div className="mt-1 text-zinc-400">RSVP · Tickets</div>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/30 px-1 py-2 text-center text-white">
              <div className="font-semibold leading-tight">Featured Item</div>
              <div className="mt-1 text-zinc-400">No discount</div>
            </div>
          </div>
        </div>

        <div className="mt-2 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
          <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-emerald-300">
            Build · Preview · Save
          </div>

          <div className="mt-1 text-sm font-semibold">
            Update Your Customer Page
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2 text-[8px]">
            <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-2">
              Example: Promotion
              <div className="mt-1 font-semibold text-white">
                Free Garlic Knots
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-2">
              Customer Preview
              <div className="mt-1 font-semibold text-white">
                Before Claim
              </div>
            </div>
          </div>

          <p className="mt-2 text-[8px] leading-relaxed text-zinc-400">
            Featured Items show a photo, description, and action button — no claim code.
          </p>
        </div>

        <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[9px] text-zinc-300">
          <div className="font-semibold text-white">
            Restaurant Details
          </div>

          <div className="mt-1 text-zinc-500">
            Photo · Phone · Address · Website · Menu · Hours
          </div>
        </div>
      </div>
    </Scene>
  );
}
